import { uniform } from './uniform.ts';
import { shapeNightmare } from './shapes.ts';
import type { DamageType } from './damage.ts';
import { sec } from './units.ts';
import type { UnsleepingId } from './unsleeping.ts';
import { BRUNI } from './unsleeping-bruni.ts';
import type { SimaCreature, SimaGuardian } from './sima.ts';

/** Sapper, ram, mangonel and archer come with era II (ERA_NIGHTMARES); slinger, crow, mourner and ogre are era I's new ones (docs/CRIATURAS.md). */
export type NightmareType =
  | 'goblin' | 'wolf' | 'skeleton' | 'wraith' | 'gargoyle' | 'slime' | 'stalker' | 'sapper' | 'ram' | 'archer'
  | 'slinger' | 'crow' | 'mourner' | 'ogre' | 'troll' | 'beetle' | 'bannerman' | 'bonesinger' | 'wyvern'
  | 'giant' | 'moth' | 'weaver' | 'spiderling' | 'oracle' | 'wyrmling' | 'mangonel' | 'mammoth' | 'dragger'
  | 'sentinel' | 'frostColossus' | 'stormHerald' | 'shellSerpent'
  | 'glassDrake' | 'shellback' | 'lidlessDreamer' | 'ashWatcher' | 'prestado';
export type BossType = 'witch' | 'shell';
/** The crypt's boss (elite incursions) is not a wave boss. */
/** Seasonal creatures (see SEASONS) only come during their holiday. */
export type SeasonalKind = 'pumpkin';
/** The Chasm's props that are struck like nightmares (docs/SIMA.md): the prism's crystals, the brood queen's cocoons, the frost lady's ice round a hero. */
export type SimaProp = 'crystal' | 'cocoon' | 'iceBlock';
export type EnemyKind = NightmareType | BossType | SeasonalKind | UnsleepingId | SimaCreature | SimaGuardian | SimaProp | 'boneWall' | 'warden' | 'magmaLord' | 'colossus' | 'dragon' | 'prowler' | 'uro' | 'hulk' | 'drinker' | 'drowned' | 'maw' | 'lure' | 'sunken';

export const NIGHTMARE_TYPES: NightmareType[] = [
  'goblin', 'wolf', 'skeleton', 'wraith', 'gargoyle', 'slime', 'stalker', 'sapper', 'ram', 'archer',
  'slinger', 'crow', 'mourner', 'ogre', 'troll', 'beetle', 'bannerman', 'bonesinger', 'wyvern',
  'giant', 'moth', 'weaver', 'spiderling', 'oracle', 'wyrmling', 'mangonel', 'mammoth', 'dragger',
  'sentinel', 'frostColossus', 'stormHerald', 'shellSerpent',
  'glassDrake', 'shellback', 'lidlessDreamer', 'ashWatcher', 'prestado',
];

export interface NightmareDef {
  type: EnemyKind;
  hp: number;
  /** Speed in fixed units per second. */
  speed: number;
  damage: number;
  reload: number;
  points: number;
  flying: boolean;
  /** Ignores ground traps (spikes, oil). */
  ignoresTraps: boolean;
  /** Armor in bp against ballista bolts and arrows. */
  pierceArmorBp: number;
  /** Damage only applies to structures (gargoyle). */
  structureHunter: boolean;
  /** Never attacks the keep. */
  avoidsKeep: boolean;
  /** Splits into `splitCount` copies with `splitHpBp` of max HP, once. */
  splitCount: number;
  splitHpBp: number;
  invisible: boolean;
  /** Melee reach (fixed). */
  reach: number;
  /** Blows up when it dies, hurting heroes and structures around it. */
  deathBlast?: { radius: number; heroDamage: number; structureDamage: number };
  /** Sapper: a wall, gate or rampart in the way isn't broken but dug under, in this many ticks (unseen meanwhile). */
  burrows?: number;
  /** Siege engine: doesn't stop for heroes in the way, can't be stunned or taunted, hits heroes for a third. */
  siege?: boolean;
  /**
   * Mangonel: outside the walls, with a wall, gate or rampart within `range` (a tower when no wall is), it stops
   * and lobs a rock at the nearest every `every` ticks.
   */
  lobs?: { range: number; every: number; dmg: number };
  /** Shoots towers from this far while it's outside the walls. */
  shootsTowers?: number;
  /** How many come together (absent: the usual 3–6). */
  group?: [number, number];
  /** No more than this many in a sector in one wave, however big the wave (absent: no cap). */
  perSector?: number;
  /** Slinger: stops to throw at the nearest hero this close (it walks on when there is none). */
  shootsHeroes?: number;
  /** A flyer that goes for the nearest hero this close instead of the keep (crows). */
  huntsHeroes?: number;
  /** Mourner: every `every` ticks it mends the other nightmares within `radius` by `bp` of their max HP. */
  mends?: { every: number; radius: number; bp: number };
  /** Its blow curses: the hero heals `healBp` of what it would for `ticks`. */
  curses?: { ticks: number; healBp: number };
  /** Ogre: its blow throws the hero back `far`, sliding over `ticks` (a jump in one tick reads as a teleport); once thrown, no blow throws it again for `immune` ticks (a pack would pin it). */
  knockback?: { far: number; ticks: number; immune: number };
  /** Ogre: with a hero within `radius`, it roars every `every` ticks: heroes there are frightened (no blows, no skills) for `ticks`. */
  roar?: { every: number; radius: number; ticks: number };
  /** Comes among the group drawn before it, not on its own (the mourner walks in the middle of a pack). */
  escorted?: boolean;
  /** Troll: mends `bpPerSec` of its max HP every second, unless fire touched it in the last `burnTicks`. */
  regen?: { bpPerSec: number; burnTicks: number };
  /**
   * Beetle: its shell lets through `takenBp` of every blow until one blow of `breakAt` or more (or a critical,
   * or a stun) cracks it, or it has taken `wear` (before the shell) in all.
   */
  shell?: { takenBp: number; breakAt: number; wear: number };
  /** Hits walls, gates and ramparts this much harder (bp): the beetle's acid. */
  wallBp?: number;
  /** Hits every structure this much harder (bp): Bruni's hollow forge. */
  structureBp?: number;
  /** Bannerman: nightmares within `radius` walk `speedBp` faster and hit `damageBp` harder; when it falls they falter (`slowBp` for `falterTicks`). */
  banner?: { radius: number; speedBp: number; damageBp: number; slowBp: number; falterTicks: number };
  /** Bonesinger: a nightmare falling within `radius` comes back as a skeleton, up to `max` per singer. */
  raises?: { radius: number; max: number };
  /** Wyvern: hovers this far from a tower and spits fire at it; the tower burns `burn.dmg` every second for `burn.ticks` unless a hero stands by it. */
  spitsFire?: { range: number; burn: { dmg: number; ticks: number } };
  /** What its blows are (absent: physical). */
  dmgType?: DamageType;
  /** Resistances (bp): +7500 takes a quarter, −5000 half again as much, 10000 next to nothing. */
  resist?: Partial<Record<DamageType, number>>;
  /** Big (the giant): stuns hold it a third as long, slows work half. */
  big?: boolean;
  /** Giant: every `every` ticks it hurls a rock at the nearest tower within `range`. */
  hurls?: { range: number; every: number; dmg: number };
  /** Giant: with a hero within `radius`, every `every` ticks it stomps: heroes there take `dmg` and are stunned for `ticks`. */
  stomp?: { every: number; radius: number; ticks: number; dmg: number };
  /** Giant: below `atBp` of its health it wakes, walks `speedBp` as fast and roars (as the ogre does). */
  wakes?: { atBp: number; speedBp: number; roar: { every: number; radius: number; ticks: number } };
  /**
   * Sleep moths: their blow puts the hero to sleep for `ticks` (no moving, no blows) unless it woke in the last
   * `immune`; an ally within `wakeRadius`, or a blow from anything but a moth, wakes it.
   */
  sleeps?: { ticks: number; immune: number; wakeRadius: number };
  /**
   * Weaver: every `every` ticks it cocoons the nearest free tower within `range`: the tower is silent for
   * `ticks`, or for `cutTicks` with a hero standing by it cutting the silk.
   */
  weaves?: { range: number; every: number; ticks: number; cutTicks: number };
  /** Its bite poisons: the hero loses `dmg` every second for `ticks`. */
  poisons?: { dmg: number; ticks: number };
  /** Bursts into `count` spiderlings when it dies. */
  brood?: { count: number };
  /** Ink oracle: every `every` ticks the other nightmares within `radius` get a bubble that soaks `hp`; a blow of `breakAt` or a critical breaks it. */
  wards?: { every: number; radius: number; hp: number; breakAt: number };
  /**
   * Burn wyrmling: every `every` ticks, a hero within `range`, it breathes a line of fire on the ground through
   * the hero, `length` long (fires of `radius` every `radius` along it) that burn heroes `dmg` a second for `ticks`.
   */
  breathes?: { every: number; range: number; length: number; radius: number; ticks: number; dmg: number };
  /** Ash mammoth: takes its own straight way to the keep, through what stands on it (a building costs no detour; it leans toward them). */
  tramples?: boolean;
  /**
   * Ash mammoth: fire (oil, a fireball, a burn) panics it, unless it calmed in the last `immune` ticks: for `ticks`
   * it turns and flees outward, trampling the nightmares within `radius` for `dmg` every second.
   */
  spooks?: { ticks: number; immune: number; radius: number; dmg: number };
  /**
   * Fallen sentinel (era IV): its tower shield stops what flies at it from the front: a shot fired within `arc`
   * (angle steps, 1024 a turn) either side of where it faces lands at `takenBp`. Blows in melee land whole.
   */
  bulwark?: { arc: number; takenBp: number };
  /** Fallen sentinel: every `every` ticks its next blow is a shield bash that stuns the hero for `ticks`. */
  bashes?: { every: number; ticks: number };
  /** Frost colossus (era IV): towers within `radius` reload `reloadBp` as slowly (the strongest cold counts). */
  chills?: { radius: number; reloadBp: number };
  /**
   * Frost colossus: every `every` ticks, a hero within `range`, it breathes frost in a cone `arc` either side of
   * that hero: the heroes in it take `dmg` and are frozen (stunned) for `ticks`.
   */
  freezes?: { every: number; range: number; arc: number; ticks: number; dmg: number };
  /** Frost colossus: a wall, gate or tower it strikes turns brittle, taking `takenBp` from everything for `ticks`. */
  brittles?: { ticks: number; takenBp: number };
  /**
   * Storm herald (era IV): it hovers `range` off a tower and calls lightning on it every `reload`; the bolt jumps to
   * up to `chain` more buildings within `chainRange` of the last, `chainBp` as hard each time.
   */
  calls?: { range: number; chain: number; chainRange: number; chainBp: number };
  /**
   * Shell serpent (era IV): it travels under the ground (no blow or shot reaches it) straight for a tower, past the
   * walls, and comes up `surface` from it; then it bites towers until it falls.
   */
  tunnels?: { surface: number };
  /** Glass drake (era V): its glass scales let through `takenBp` of any blow under `breakAt` (the dragon's kin: heavy blows crack it). */
  glassScales?: { breakAt: number; takenBp: number };
  /**
   * Glass drake: it hovers `range` off a tower and every `reload` sweeps a cone of fire `arc` either side of it:
   * every building in the cone takes the blow and burns `burnDmg` a second for `burnTicks`.
   */
  sweeps?: { range: number; arc: number; burnTicks: number; burnDmg: number };
  /** Shellback (era V): what is shot at it from afar lands at `takenBp` (blows in melee land whole). */
  plated?: { takenBp: number };
  /** Shellback: as its health falls past each of `at` (bp), the nightmares clinging to its back drop off: `count` of `kinds` around it. */
  carries?: { at: number[]; count: number; kinds: NightmareType[] };
  /**
   * Lidless dreamer (era V): every `every` ticks it opens its eyes: towers within `radius` fall silent and heroes
   * there fall asleep for `ticks`.
   */
  dreams?: { every: number; radius: number; ticks: number };
  /** Lidless dreamer: a blow of `at` or more makes it vanish and come back `far` away, at most once every `cooldown`. */
  blinks?: { at: number; far: number; cooldown: number };
  /** Ash watcher (era V): of the heroes within `huntsHeroes`, it dives on the one with the least health left (not the nearest). */
  preysOnWeak?: boolean;
  /** One of the Unsleeping (docs/ANTIHEROES.md): a guardian of the old garrison, his own fight in the wave. */
  unsleeping?: UnsleepingId;
  /** Ash watcher: its blow strips the hero's boons (shields, wards, blessings, inspiration) and mends it `healBp` of its life when it took any. */
  stealsBoons?: { healBp: number };
}

const n0 = {
  flying: false,
  ignoresTraps: false,
  pierceArmorBp: 0,
  structureHunter: false,
  avoidsKeep: false,
  splitCount: 0,
  splitHpBp: 0,
  invisible: false,
  reach: 700,
};

/**
 * After a nightmare's roar frightens a hero (an ogre, a woken giant), the hero holds its nerve this long once the
 * fright is over: no new roar lands meanwhile. Without it a pack of ogres kept a hero frightened most of a wave
 * (players, 2026-10-31: «no me deja tirar habilidades»). The Unsleeping's own frights are theirs and don't count.
 */
export const FEAR = { graceTicks: sec(4) };

export const NIGHTMARES: Record<EnemyKind, NightmareDef> = uniform({
  goblin: { ...n0, type: 'goblin', hp: 60, speed: 1200, damage: 8, reload: 12, points: 3 },
  wolf: { ...n0, type: 'wolf', hp: 45, speed: 2200, damage: 6, reload: 9, points: 4, ignoresTraps: true },
  skeleton: { ...n0, type: 'skeleton', hp: 120, speed: 900, damage: 12, reload: 14, points: 9, pierceArmorBp: 4000 },
  wraith: { ...n0, type: 'wraith', hp: 70, speed: 1400, damage: 10, reload: 12, points: 7, flying: true },
  gargoyle: { ...n0, type: 'gargoyle', hp: 160, speed: 1100, damage: 20, reload: 18, points: 12, flying: true, structureHunter: true, avoidsKeep: true },
  slime: { ...n0, type: 'slime', hp: 200, speed: 600, damage: 14, reload: 18, points: 14, splitCount: 2, splitHpBp: 4000 },
  stalker: { ...n0, type: 'stalker', hp: 80, speed: 1300, damage: 15, reload: 12, points: 8, invisible: true },
  /** Era II: digs under the wall or gate in its way and comes up inside. */
  sapper: { ...n0, type: 'sapper', hp: 90, speed: 1300, damage: 9, reload: 12, points: 9, burrows: 36 },
  /** Era II: a battering ram of bone; slow, hard to pierce, straight for the first gate or wall. */
  ram: { ...n0, type: 'ram', hp: 520, speed: 650, damage: 55, reload: 30, points: 30, pierceArmorBp: 4000, siege: true, group: [1, 2] },
  /** Era II: a mangonel of bone; stops far off and lobs rocks at the walls first, then the towers. */
  mangonel: { ...n0, type: 'mangonel', hp: 420, speed: 550, damage: 20, reload: 30, points: 40, pierceArmorBp: 4000, siege: true, lobs: { range: 8000, every: sec(5), dmg: 40 }, group: [1, 1] },
  /** Era II: an ash mammoth, slow and huge; never looks at a hero, walks straight for the keep through whatever stands in the way. Fire turns it back. */
  mammoth: {
    ...n0,
    type: 'mammoth',
    hp: 3000,
    speed: 420,
    damage: 150,
    reload: 24,
    points: 90,
    reach: 1000,
    siege: true,
    big: true,
    tramples: true,
    spooks: { ticks: sec(4), immune: sec(10), radius: 1200, dmg: 40 },
    group: [1, 1],
    perSector: 2,
  },
  /** Era II: shoots towers from outside the walls, farther than a ballista or a catapult reaches. */
  archer: { ...n0, type: 'archer', hp: 60, speed: 1250, damage: 14, reload: 30, points: 11, shootsTowers: 6300, group: [2, 3] },
  /** Era I: stays behind and slings stones at the heroes; frail up close. */
  slinger: { ...n0, type: 'slinger', hp: 50, speed: 1250, damage: 9, reload: 26, points: 6, shootsHeroes: 4200, group: [2, 3] },
  /** Era I: soot crows in flocks; over the walls, pecking at the nearest hero (the keep when there is none). */
  crow: { ...n0, type: 'crow', hp: 20, speed: 2100, damage: 4, reload: 10, points: 2, flying: true, huntsHeroes: 5000, group: [6, 10] },
  /** Era I: walks in the middle of a pack mending it with threads of ink; its claw curses (the hero heals half). Kill it first. */
  mourner: {
    ...n0,
    type: 'mourner',
    hp: 130,
    speed: 1150,
    damage: 8,
    reload: 14,
    points: 14,
    mends: { every: sec(2), radius: 2200, bp: 800 },
    curses: { ticks: sec(5), healBp: 5000 },
    group: [1, 1],
    escorted: true,
  },
  /** Era I: a small giant. Its sweep throws heroes back, and its roar frightens them. */
  ogre: { ...n0, type: 'ogre', hp: 700, speed: 750, damage: 38, reload: 26, points: 40, reach: 900, knockback: { far: 800, ticks: 4, immune: sec(3) }, roar: { every: sec(10), radius: 2600, ticks: sec(2) }, group: [1, 1] },
  /** Era II: a bog troll that knits back together unless it burns (oil, fire, the mage). */
  troll: { ...n0, type: 'troll', hp: 380, speed: 1000, damage: 22, reload: 18, points: 28, reach: 850, regen: { bpPerSec: 600, burnTicks: sec(4) }, group: [1, 2] },
  /** Era II: an ink beetle under a shell that only a heavy blow cracks; its acid eats walls. */
  beetle: { ...n0, type: 'beetle', hp: 300, speed: 800, damage: 16, reload: 20, points: 32, shell: { takenBp: 2000, breakAt: 40, wear: 160 }, wallBp: 20000, group: [1, 2] },
  /** Era II: a dead knight's banner; the pack around it marches faster and hits harder, and falters when it falls. */
  bannerman: {
    ...n0,
    type: 'bannerman',
    hp: 240,
    speed: 1000,
    damage: 14,
    reload: 14,
    points: 22,
    banner: { radius: 2600, speedBp: 2500, damageBp: 2500, slowBp: 5000, falterTicks: sec(3) },
    group: [1, 1],
    escorted: true,
  },
  /** Era II: sings the fallen back up as skeletons. Hunt it. */
  bonesinger: { ...n0, type: 'bonesinger', hp: 170, speed: 1000, damage: 10, reload: 14, points: 26, raises: { radius: 2800, max: 6 }, group: [1, 1], escorted: true },
  /** Era II: a wyvern of ash that hovers off a tower and sets it burning. */
  wyvern: { ...n0, type: 'wyvern', hp: 220, speed: 1300, damage: 18, reload: 30, points: 36, flying: true, spitsFire: { range: 3200, burn: { dmg: 5, ticks: sec(6) } }, group: [1, 2] },
  /** Era III: a giant dreamt out of the titan's own shell, asleep on its feet. Rocks at the towers, a stomp that stuns; at half health it wakes. */
  giant: {
    ...n0,
    type: 'giant',
    hp: 1500,
    speed: 550,
    damage: 50,
    reload: 30,
    points: 80,
    reach: 1300,
    pierceArmorBp: 2000,
    big: true,
    hurls: { range: 7000, every: sec(6), dmg: 35 },
    stomp: { every: sec(7), radius: 1800, ticks: sec(1), dmg: 20 },
    wakes: { atBp: 5000, speedBp: 14000, roar: { every: sec(10), radius: 3000, ticks: sec(2) } },
    group: [1, 1],
  },
  /** Era III: sleep moths in swarms; the hero they wrap falls asleep until an ally comes. */
  moth: { ...n0, type: 'moth', hp: 22, speed: 1800, damage: 3, reload: 12, points: 3, flying: true, huntsHeroes: 5000, sleeps: { ticks: sec(3), immune: sec(8), wakeRadius: 900 }, group: [6, 10] },
  /** Era III: cocoons towers in silk, bites with poison, and bursts into spiderlings. */
  weaver: {
    ...n0,
    type: 'weaver',
    hp: 260,
    speed: 1100,
    damage: 10,
    reload: 16,
    points: 30,
    weaves: { range: 3200, every: sec(8), ticks: sec(20), cutTicks: sec(1) },
    poisons: { dmg: 3, ticks: sec(6) },
    brood: { count: 4 },
    group: [1, 1],
  },
  /** What a weaver leaves when it dies (never drawn on its own). */
  spiderling: { ...n0, type: 'spiderling', hp: 18, speed: 1800, damage: 4, reload: 10, points: 1 },
  /** Plunder (docs/MISIONES.md): a small thing of shadow that never fights; it hauls the caches on the ground back to its hole. Only plunder sends it. */
  dragger: { ...n0, type: 'dragger', hp: 40, speed: 1500, damage: 0, reload: 12, points: 3 },
  /** Era III: a bubble of ink over the nightmares around it soaks the blows; heavy ones and criticals burst it. */
  oracle: { ...n0, type: 'oracle', hp: 170, speed: 950, damage: 8, reload: 14, points: 28, wards: { every: sec(6), radius: 2400, hp: 50, breakAt: 35 }, group: [1, 1], escorted: true },
  /** Era III: a young of the dragon of the Burn, of black glass. It breathes a line of fire across the ground. */
  wyrmling: {
    ...n0,
    type: 'wyrmling',
    hp: 260,
    speed: 1500,
    damage: 14,
    reload: 24,
    points: 44,
    flying: true,
    huntsHeroes: 8000,
    breathes: { every: sec(8), range: 3600, length: 4000, radius: 500, ticks: sec(4), dmg: 5 },
    group: [1, 1],
  },
  /** Era IV: a knight of the old garrison, dreamt twisted; a wall of three behind tower shields that stop shots from the front. */
  sentinel: {
    ...n0,
    type: 'sentinel',
    hp: 700,
    speed: 800,
    damage: 40,
    reload: 18,
    points: 34,
    reach: 900,
    pierceArmorBp: 5000,
    bulwark: { arc: 171, takenBp: 1500 },
    bashes: { every: sec(6), ticks: sec(1) },
    resist: { shadow: 5000 },
    group: [3, 3],
    perSector: 3,
  },
  /** Era IV: a giant of ice in its own fog; towers near it freeze up, its breath freezes heroes, its blows crack stone. */
  frostColossus: {
    ...n0,
    type: 'frostColossus',
    hp: 2600,
    speed: 480,
    damage: 90,
    reload: 28,
    points: 100,
    reach: 1300,
    pierceArmorBp: 2500,
    big: true,
    dmgType: 'frost',
    resist: { frost: 10000, fire: -5000 },
    chills: { radius: 4000, reloadBp: 15000 },
    freezes: { every: sec(8), range: 3500, arc: 128, ticks: sec(2), dmg: 30 },
    brittles: { ticks: sec(10), takenBp: 15000 },
    group: [1, 1],
    perSector: 1,
  },
  /** Era IV: a storm cloud with a hooded face; hovers off a tower and calls lightning that leaps from building to building. */
  stormHerald: {
    ...n0,
    type: 'stormHerald',
    hp: 900,
    speed: 1000,
    damage: 45,
    reload: sec(4),
    points: 60,
    flying: true,
    dmgType: 'lightning',
    resist: { lightning: 10000 },
    calls: { range: 5000, chain: 3, chainRange: 3000, chainBp: 6000 },
    group: [1, 2],
    perSector: 2,
  },
  /** Era IV: a serpent of rock from between the shell's plates; it comes up inside the walls by a tower and eats it with acid. */
  shellSerpent: {
    ...n0,
    type: 'shellSerpent',
    hp: 1400,
    speed: 1100,
    damage: 70,
    reload: 20,
    points: 70,
    reach: 900,
    ignoresTraps: true,
    dmgType: 'acid',
    resist: { acid: 10000 },
    tunnels: { surface: 1500 },
    group: [1, 1],
    perSector: 2,
  },
  /** Era V: an adult child of the Burn, of black glass with the fire inside; sweeps whole rows of buildings with fire. */
  glassDrake: {
    ...n0,
    type: 'glassDrake',
    hp: 1800,
    speed: 1100,
    damage: 60,
    reload: sec(6),
    points: 120,
    flying: true,
    big: true,
    dmgType: 'fire',
    resist: { fire: 10000 },
    glassScales: { breakAt: 60, takenBp: 3000 },
    sweeps: { range: 5000, arc: 160, burnTicks: sec(6), burnDmg: 12 },
    group: [1, 1],
    perSector: 1,
  },
  /** Era V: a piece of the shell that walks, two cells wide, with nightmares clinging to its back that drop off as it breaks. */
  shellback: {
    ...n0,
    type: 'shellback',
    hp: 6000,
    speed: 380,
    damage: 120,
    reload: 30,
    points: 160,
    reach: 1500,
    pierceArmorBp: 5000,
    big: true,
    plated: { takenBp: 5000 },
    carries: { at: [7500, 5000, 2500], count: 4, kinds: ['goblin', 'skeleton', 'sentinel'] },
    group: [1, 1],
    perSector: 1,
  },
  /** Era V: a sleeper crowned with closed eyes; when they open, the towers around it fall silent and the heroes sleep. */
  lidlessDreamer: {
    ...n0,
    type: 'lidlessDreamer',
    hp: 1200,
    speed: 600,
    flying: true,
    damage: 30,
    reload: 20,
    points: 90,
    dmgType: 'shadow',
    resist: { shadow: 10000 },
    dreams: { every: sec(9), radius: 3500, ticks: sec(3) },
    blinks: { at: 80, far: 3000, cooldown: sec(6) },
    group: [1, 1],
    perSector: 1,
  },
  /**
   * Caras prestadas: a small nightmare that copies faces, only of someone who isn't there. Tall and thin under the
   * borrowed face; it comes out when a hero tears the face off (never from a wave's budget).
   */
  prestado: {
    ...n0,
    type: 'prestado',
    hp: 900,
    speed: 1600,
    damage: 34,
    reload: 14,
    points: 0,
    dmgType: 'shadow',
    huntsHeroes: 6000,
    group: [1, 1],
    perSector: 1,
  },
  /** Era V: a fallen angel of the Order with wings of ash; it dives on the weakest hero and tears its boons away. */
  ashWatcher: {
    ...n0,
    type: 'ashWatcher',
    hp: 800,
    speed: 1700,
    damage: 50,
    reload: sec(3),
    points: 70,
    flying: true,
    dmgType: 'shadow',
    huntsHeroes: 7000,
    preysOnWeak: true,
    stealsBoons: { healBp: 2000 },
    group: [1, 2],
    perSector: 2,
  },
  /** Halloween: a lantern-headed thing that bursts into burning seeds when it falls — don't kill it against the wall or in the middle of the party. */
  pumpkin: { ...n0, type: 'pumpkin', hp: 70, speed: 1500, damage: 8, reload: 12, points: 5, deathBlast: { radius: 1300, heroDamage: 35, structureDamage: 45 } },
  witch: { ...n0, type: 'witch', hp: 3000, speed: 800, damage: 30, reload: 12, points: 0, reach: 900 },
  shell: { ...n0, type: 'shell', hp: 9000, speed: 500, damage: 60, reload: 18, points: 0, reach: 1100 },
  boneWall: { ...n0, type: 'boneWall', hp: 400, speed: 0, damage: 0, reload: 0, points: 0 },
  warden: { ...n0, type: 'warden', hp: 6000, speed: 700, damage: 26, reload: 16, points: 0, reach: 1000 },
  /** Ascuas (incursion level II): a lord of molten rock that closes the last wave and calls eruptions. */
  magmaLord: { ...n0, type: 'magmaLord', hp: 1600, speed: 650, damage: 30, reload: 18, points: 0, reach: 1400 },
  /** Obsidiana (level III) and the deep crypt's last hall: a colossus of black glass that shines and turns blows back. */
  colossus: { ...n0, type: 'colossus', hp: 2400, speed: 600, damage: 36, reload: 20, points: 0, reach: 1300 },
  /** The Furrows (docs/LABERINTOS.md): something big that prowls the corridors and never falls; hit hard enough, it goes away a while. Its health is a stagger bar (MAZE.prowler). */
  prowler: { ...n0, type: 'prowler', hp: 500, speed: 1200, damage: 40, reload: 18, points: 0, reach: 1000 },
  /** El Cuerno 3 (docs/SAGAS.md): the Old Uro, a bull of ash that charges whatever moves. Its life comes from the hunt (SIDE.hunt); it never strikes, it charges (battle/hunt.ts). */
  uro: { ...n0, type: 'uro', hp: 9000, speed: 700, damage: 0, reload: 0, points: 0, reach: 900 },
  /** The saga's last foe (docs/LORE.md): the dragon of the Burn, the titan's oldest nightmare. Its health comes from the act. */
  /** The Unsleeping (docs/ANTIHEROES.md): Ser Corvin, the Knight of the Gate. Health and blows come from the party (UNSLEEPING). */
  corvin: { ...n0, type: 'corvin', hp: 6000, speed: 1500, damage: 70, reload: sec(1.2), points: 0, reach: 1000, big: true, unsleeping: 'corvin' },
  // Kaela shoots thorns from afar (her kit, KAELA.shot); `reach` is her bear's, up close.
  kaela: { ...n0, type: 'kaela', hp: 6000, speed: 1400, damage: 60, reload: sec(1.25), points: 0, reach: 1000, big: true, unsleeping: 'kaela' },
  lucan: { ...n0, type: 'lucan', hp: 5400, speed: 1300, damage: 55, reload: sec(1.5), points: 0, reach: 1000, big: true, unsleeping: 'lucan' },
  edda: { ...n0, type: 'edda', hp: 7200, speed: 1350, damage: 75, reload: sec(1.3), points: 0, reach: 1000, big: true, unsleeping: 'edda' },
  vela: { ...n0, type: 'vela', hp: 4800, speed: 1550, damage: 50, reload: sec(1.3), points: 0, reach: 1000, big: true, unsleeping: 'vela' },
  ansel: { ...n0, type: 'ansel', hp: 5100, speed: 1400, damage: 55, reload: sec(1.5), points: 0, reach: 1000, big: true, unsleeping: 'ansel' },
  /** Bruni, the Hollow Forger: a dwarf with a forge-cannon, shooting from afar; triple on structures. */
  bruni: { ...n0, type: 'bruni', hp: 6000, speed: 1300, damage: 55, reload: sec(1.5), points: 0, reach: 5000, big: true, unsleeping: 'bruni', structureBp: BRUNI.structureBp },
  /** Ragna, the Horn: the fastest of them, a barbarian's axe. */
  ragna: { ...n0, type: 'ragna', hp: 6000, speed: 2100, damage: 75, reload: sec(1.1), points: 0, reach: 1000, big: true, unsleeping: 'ragna' },
  /** La Orilla (docs/SAGAS.md): the Drowned, a bloated fisherman with nets and stones tied on. Slow and tough; it grabs a hero and drags them out to sea (SIDE.drowned). */
  drowned: { ...n0, type: 'drowned', hp: 900, speed: 750, damage: 30, reload: 18, points: 0, reach: 900 },
  /** La Orilla 3 (docs/SAGAS.md): the Maw, a fish of ink the size of a ship that never leaves the water's edge; its life comes from the act (SIDE.maw). */
  maw: { ...n0, type: 'maw', hp: 9000, speed: 0, damage: 0, reload: 18, points: 0, reach: 1400, big: true },
  /** …and the stalk of its light, over its mouth: breaking it puts the light out a while. */
  lure: { ...n0, type: 'lure', hp: 800, speed: 0, damage: 0, reload: 12, points: 0 },
  /** La Orilla 5 (docs/SAGAS.md): the Drowned Dragon, the real dragon's bones raised by a nightmare inside them. Its life comes from the act (SIDE.sunken). */
  sunken: { ...n0, type: 'sunken', hp: 20000, speed: 800, damage: 85, reload: sec(1.6), points: 0, reach: 1500, big: true },
  /** El pozo 2 (docs/SAGAS.md): the Drinker, a leech of ink the size of a cart at the well's vein. Its life comes from the act; its round from SIDE.drinker. */
  drinker: { ...n0, type: 'drinker', hp: 3000, speed: 1100, damage: 22, reload: 14, points: 0, reach: 1100, big: true },
  /** Las vías 4 (docs/SAGAS.md): the biggest of the automatons Bruni turned, when he already fell elsewhere. Its life comes from the act. */
  hulk: { ...n0, type: 'hulk', hp: 6000, speed: 800, damage: 70, reload: sec(1.4), points: 0, reach: 1100, big: true },
  dragon: { ...n0, type: 'dragon', hp: 20000, speed: 900, damage: 95, reload: 20, points: 0, reach: 1700 },
  // The Chasm (docs/SIMA.md): its own creatures, one per new stratum. Their moves are in SIMA_CREATURE.
  /** Cisterns: black leeches out of the deep water, in a swarm; they cling and drink. */
  leech: { ...n0, type: 'leech', hp: 40, speed: 1500, damage: 5, reload: 10, points: 2, poisons: { dmg: 3, ticks: 36 }, group: [6, 10] },
  /** Buried wood: a scarecrow of roots, slow and hard; its blow roots the hero in front of it. */
  rootwight: { ...n0, type: 'rootwight', hp: 380, speed: 600, damage: 22, reload: 20, points: 18, reach: 900, group: [1, 2] },
  /** Grotto: a walking puffball; it bursts in sleeping spores when it falls. */
  puffball: { ...n0, type: 'puffball', hp: 90, speed: 700, damage: 6, reload: 16, points: 7, group: [2, 4] },
  /** Frost veins: an ooze of ice; rime stays where it crawls. */
  rime: { ...n0, type: 'rime', hp: 180, speed: 650, damage: 12, reload: 16, points: 10, group: [2, 3] },
  /** Geode: a thing of crystal that splits in two smaller ones the first time it falls. */
  shardling: { ...n0, type: 'shardling', hp: 150, speed: 1100, damage: 14, reload: 14, points: 10, splitCount: 2, splitHpBp: 4500, group: [2, 3] },
  /** Archive: a scribe that writes runes under heroes from afar; whoever is on one when it closes is cursed. */
  scribe: { ...n0, type: 'scribe', hp: 90, speed: 1100, damage: 8, reload: 20, points: 12, shootsHeroes: 5500, group: [1, 2] },
  /** Dead forge: a figure of red-hot metal leaving molten pools; frost makes it brittle. */
  molten: { ...n0, type: 'molten', hp: 300, speed: 750, damage: 20, reload: 18, points: 16, group: [1, 2] },
  /** Ribs: small bone worms that come out of the floor in groups. */
  boneworm: { ...n0, type: 'boneworm', hp: 35, speed: 1600, damage: 6, reload: 10, points: 2, group: [3, 5] },
  // The Chasm's guardians: their health comes from the floor (SIMA.guardianHp), their moves from SIMA_BOSS.
  leechMother: { ...n0, type: 'leechMother', hp: 6000, speed: 300, damage: 34, reload: 18, points: 0, reach: 1500, big: true },
  rootking: { ...n0, type: 'rootking', hp: 6000, speed: 550, damage: 40, reload: 20, points: 0, reach: 1400, big: true },
  sporeMother: { ...n0, type: 'sporeMother', hp: 6000, speed: 0, damage: 30, reload: 16, points: 0, reach: 1900, big: true },
  frostLady: { ...n0, type: 'frostLady', hp: 6000, speed: 900, damage: 32, reload: 16, points: 0, reach: 1200, big: true },
  prism: { ...n0, type: 'prism', hp: 6000, speed: 500, damage: 36, reload: 18, points: 0, reach: 1300, big: true },
  archivist: { ...n0, type: 'archivist', hp: 6000, speed: 600, damage: 28, reload: 16, points: 0, reach: 1200, big: true },
  bellows: { ...n0, type: 'bellows', hp: 6000, speed: 350, damage: 42, reload: 20, points: 0, reach: 1500, big: true },
  broodQueen: { ...n0, type: 'broodQueen', hp: 6000, speed: 700, damage: 34, reload: 16, points: 0, reach: 1300, big: true },
  marrowWorm: { ...n0, type: 'marrowWorm', hp: 6000, speed: 900, damage: 44, reload: 18, points: 0, reach: 1400, big: true },
  /** The prism's crystals, the brood queen's cocoons, the frost lady's ice: they stand still and are struck like nightmares. */
  crystal: { ...n0, type: 'crystal', hp: 260, speed: 0, damage: 0, reload: 0, points: 0 },
  cocoon: { ...n0, type: 'cocoon', hp: 150, speed: 0, damage: 0, reload: 0, points: 0 },
  iceBlock: { ...n0, type: 'iceBlock', hp: 120, speed: 0, damage: 0, reload: 0, points: 0 },
}, shapeNightmare);

/**
 * What each nightmare's blows are and what it resists (docs/CRIATURAS.md §3). Kept apart from the stats
 * above so the table reads as one picture of strengths and weaknesses.
 */
const TYPES: Partial<Record<EnemyKind, Pick<NightmareDef, 'dmgType' | 'resist'>>> = {
  skeleton: { resist: { poison: 7500, holy: -5000 } },
  wraith: { dmgType: 'shadow', resist: { physical: 1500, holy: -5000 } },
  gargoyle: { resist: { physical: 1000, lightning: -3000 } },
  slime: { dmgType: 'acid', resist: { acid: 10000, frost: -5000 } },
  stalker: { dmgType: 'shadow', resist: { holy: -3000 } },
  mourner: { dmgType: 'shadow', resist: { shadow: 5000, holy: -3000 } },
  troll: { resist: { fire: -5000, poison: 5000 } },
  beetle: { dmgType: 'acid', resist: { acid: 7500, lightning: -2500 } },
  bannerman: { resist: { poison: 7500, holy: -3000 } },
  bonesinger: { dmgType: 'shadow', resist: { poison: 7500, holy: -5000 } },
  wyvern: { dmgType: 'fire', resist: { fire: 7500, frost: -3000 } },
  giant: { resist: { frost: -2500, poison: 3000 } },
  moth: { dmgType: 'shadow', resist: { fire: -5000 } },
  weaver: { dmgType: 'poison', resist: { poison: 7500, fire: -3000 } },
  spiderling: { dmgType: 'poison', resist: { poison: 7500, fire: -3000 } },
  oracle: { dmgType: 'shadow', resist: { shadow: 5000, lightning: -3000 } },
  wyrmling: { dmgType: 'fire', resist: { fire: 7500, frost: -3000 } },
  witch: { dmgType: 'fire', resist: { fire: 5000 } },
  warden: { dmgType: 'shadow' },
  magmaLord: { dmgType: 'fire', resist: { fire: 7500, frost: -2500 } },
  dragon: { dmgType: 'fire', resist: { fire: 10000 } },
  corvin: { resist: { shadow: 5000, holy: -3000 } },
  kaela: { dmgType: 'poison', resist: { poison: 5000, fire: -3000 } },
  lucan: { dmgType: 'shadow', resist: { shadow: 5000, holy: -3000 } },
  edda: { dmgType: 'holy', resist: { holy: 5000, shadow: -3000 } },
  vela: { resist: { shadow: 5000, holy: -3000 } },
  ansel: { dmgType: 'fire', resist: { fire: 7500, shadow: 5000, holy: -3000 } },
  bruni: { resist: { shadow: 5000, fire: 5000, holy: -3000 } },
  ragna: { resist: { shadow: 5000, frost: 3000, holy: -3000 } },
  maw: { dmgType: 'shadow', resist: { frost: 5000, lightning: -3000 } },
  sunken: { dmgType: 'fire', resist: { fire: 7500, frost: 3000, lightning: -3000 } },
  drowned: { resist: { physical: 2000, frost: 5000, lightning: -3000 } },
  drinker: { dmgType: 'shadow', resist: { poison: 5000 } },
  hulk: { resist: { poison: 10000, shadow: 3000 } },
  leech: { resist: { poison: 7500, fire: -3000 } },
  rootwight: { resist: { fire: -5000, poison: 5000 } },
  puffball: { dmgType: 'poison', resist: { poison: 7500, fire: -5000 } },
  rime: { dmgType: 'frost', resist: { frost: 10000, fire: -5000 } },
  shardling: { resist: { lightning: 5000, physical: -2500 } },
  scribe: { dmgType: 'shadow', resist: { shadow: 5000, fire: -3000, holy: -3000 } },
  molten: { dmgType: 'fire', resist: { fire: 7500, frost: -5000 } },
  boneworm: { resist: { poison: 7500, holy: -3000 } },
  leechMother: { dmgType: 'poison', resist: { poison: 7500, lightning: -2500 } },
  rootking: { resist: { fire: -3000, poison: 5000 } },
  sporeMother: { dmgType: 'poison', resist: { poison: 7500, fire: -3000 } },
  frostLady: { dmgType: 'frost', resist: { frost: 10000, fire: -3000 } },
  prism: { dmgType: 'lightning', resist: { lightning: 7500, physical: -2000 } },
  archivist: { dmgType: 'shadow', resist: { shadow: 7500, holy: -3000 } },
  bellows: { dmgType: 'fire', resist: { fire: 7500, frost: -3000 } },
  broodQueen: { dmgType: 'poison', resist: { poison: 7500, fire: -3000 } },
  marrowWorm: { resist: { poison: 5000, holy: -2500 } },
};
for (const [k, v] of Object.entries(TYPES)) Object.assign(NIGHTMARES[k as EnemyKind], v);

/** Corruption grades. MVP: 1 shadow, 2 ink. */
export interface TierDef {
  hpBp: number;
  speedBp: number;
  /** Regenerates `regenBpPerSec` of max HP per second after `regenDelay` ticks without damage. */
  regenBpPerSec: number;
  regenDelay: number;
}
export const TIERS: Record<1 | 2 | 3, TierDef> = {
  1: { hpBp: 10000, speedBp: 10000, regenBpPerSec: 0, regenDelay: 0 },
  2: { hpBp: 16000, speedBp: 10500, regenBpPerSec: 200, regenDelay: sec(3) },
  // Grade 3 only appears as an elite (warlord) of a grade-2 wave in the MVP.
  3: { hpBp: 22000, speedBp: 11000, regenBpPerSec: 200, regenDelay: sec(3) },
};

/** Warlord: one grade up, HP ×2, guaranteed drop. */
export const ELITE_HP_BP = 20000;
export const ELITE_DUST = 30;
export const ELITE_FROM_MAREA = 1; // marea index (0-based) → "desde la Marea 2"

/** Dust per kill = floor(points / 4), min 1. */
export const dustForPoints = (points: number): number => Math.max(1, Math.floor(points / 4));

export interface BossDef {
  type: BossType;
  hpBase: number;
  marea: number;
  wave: number;
}
export const BOSSES: Record<BossType, BossDef> = {
  witch: { type: 'witch', hpBase: 3000, marea: 2, wave: 5 },
  shell: { type: 'shell', hpBase: 9000, marea: 5, wave: 5 },
};
export const WITCH = {
  curseEvery: sec(10),
  curseRange: 6000,
  summonAtBp: 5000,
  summonCount: 6,
};
export const SHELL = {
  spitEvery: sec(6),
  spitCount: 2,
  phase2AtBp: 6000,
  boneWalls: 3,
  phase3AtBp: 2500,
  phase3SpeedBp: 13000,
};
